import {
    EngineRuntimeName,
    EngineRuntime,
    WidgetName,
    EngineRuntimeFeedButtonVisualSettings,
} from "@kaltura/unisphere-in-app-messaging-core";
import { UnisphereRuntimeVisual, useGetUnisphereWorkspace } from "@unisphere/runtime-react";
import { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";

const buttonVisualType = "feedButton";

interface InAppMessagingButtonProps {
    settings?: EngineRuntimeFeedButtonVisualSettings["predefined"];
    mobileViewBreakpoint?: number;
}

export const InAppMessagingButton = ({ settings, mobileViewBreakpoint }: InAppMessagingButtonProps) => {
    const [runtime, setRuntime] = useState<EngineRuntime>();
    const [buttonVisible, setButtonVisible] = useState(false);

    const buttonSettings = useMemo<EngineRuntimeFeedButtonVisualSettings>(
        () => ({
            predefined: settings ?? { type: "mediaspace-ds" },
            mobileViewBreakpoint,
        }),
        [settings, mobileViewBreakpoint]
    );

    const { unisphereWorkspace } = useGetUnisphereWorkspace();
    useEffect(() => {
        if (!unisphereWorkspace) {
            return;
        }

        (async () => {
            try {
                const runtime = await unisphereWorkspace.getRuntimeAsync<EngineRuntime>(WidgetName, EngineRuntimeName);
                if (runtime) {
                    setRuntime(runtime);
                }
            }
            catch (error: unknown) {
                console.error("Failed to get InAppMessaging runtime:", error);
            }
        })();
    }, [unisphereWorkspace]);

    useEffect(() => {
        if (!runtime) {
            return;
        }

        // Note: the method returns a disposer, so return it
        return runtime.onVisualVisibilityChanged(
            (data) => {
                if (data.visualType === buttonVisualType) {
                    setButtonVisible(data.visible);
                }
            },
            { replayLastValue: true }
        );
    }, [runtime]);

    return (
        <StyledWrapper
            className={"kms-ds-in-app-messaging-button"}
            visible={buttonVisible}
            isDS={settings?.type !== "mediaspace-non-ds"}
        >
            <UnisphereRuntimeVisual
                runtimeName={EngineRuntimeName}
                widgetName={WidgetName}
                visualType={buttonVisualType}
                visualSettings={buttonSettings}
            />
        </StyledWrapper>
    );
};

const StyledWrapper = styled("div")<{ visible: boolean; isDS: boolean }>(({ theme, visible, isDS }) => ({
    /*
     * DS - header buttons are separated by margins on individual buttons.
     * Non-DS - every header button has equal padding from both sides, and it separates buttons from each other.
     *
     * No active campaign - no spacing is needed no matter DS or not DS.
     */
    marginRight: theme.spacing(visible && isDS ? 1 : 0),
}));
